import { DataLayer } from "@sazka/utils/data-layer";
import { documentReady } from "@sazka/utils/document-ready";

type Promo = {
    id: string;
    name: string;
    creative: string;
    position: string;
};

const impressedKeys = new Set<string>();

function initPromoCardAnalytics() {
    trackInitialImpressions();
    setupCarouselTracking();
    setupClickTracking();
}

function getWidgetPosition(element: Element) {
    const widgetEl = element.closest("[data-cms-widget]") as Element | null;
    const widgets = Array.from(document.querySelectorAll("[data-cms-widget]"));
    const idx = widgets.indexOf(widgetEl as Element);
    return idx >= 0 ? idx + 1 : 0;
}

function extractPromoData(
    element: Element,
    widgetPosition: number,
    widgetType: "banner" | "carousel",
    slideNumber?: number
): Promo {
    const codename = element.getAttribute("data-a-codename") || "";
    const heading = element.querySelector(".js-ds-promo-card-heading")?.textContent?.trim() || "";
    const description = element.querySelector(".ds-promo-banner-new__description")?.textContent?.trim() || "";
    const creative = [heading, description].filter(Boolean).join(";");

    return {
        id: codename,
        name: codename,
        creative,
        position: slideNumber ? `${widgetPosition}.${widgetType}.${slideNumber}` : `${widgetPosition}.${widgetType}`,
    };
}

function promoKey(p: Promo) {
    return `${p.id}|${p.position}`;
}

function trackImpressions(promos: Promo[]) {
    if (!promos || !promos.length) return;

    DataLayer.push({
        action: {
            impress: {
                promos: null,
            },
        },
    });

    DataLayer.push({
        event: "action.impress.promos",
        action: {
            impress: {
                promos,
            },
        },
    });
}

function trackInitialImpressions() {
    const promos: Promo[] = [];

    // Regular promo cards (non-carousel)
    document.querySelectorAll(".ds-promo-banner-new:not(.ds-promo-banner-new--carousel)").forEach((banner) => {
        const widgetPosition = getWidgetPosition(banner);
        const activeItem = banner.querySelector(".js-ds-promo-card-item-active");
        if (activeItem) {
            const promo = extractPromoData(activeItem, widgetPosition, "banner");
            const key = promoKey(promo);
            if (!impressedKeys.has(key)) {
                impressedKeys.add(key);
                promos.push(promo);
            }
        }
    });

    // First slide of each carousel
    document.querySelectorAll(".ds-promo-banner-new--carousel").forEach((carousel) => {
        const widgetPosition = getWidgetPosition(carousel);
        const activeItem = carousel.querySelector(".js-ds-promo-card-item-active");
        if (activeItem) {
            const promo = extractPromoData(activeItem, widgetPosition, "carousel", 1);
            const key = promoKey(promo);
            if (!impressedKeys.has(key)) {
                impressedKeys.add(key);
                promos.push(promo);
            }
        }
    });

    trackImpressions(promos);
}

function setupClickTracking() {
    document.addEventListener(
        "click",
        (e) => {
            const target = e.target as Element | null;
            if (!target || !("closest" in target)) return;

            const linkOverlay = target.closest(".ds-promo-banner-new__link-overlay");
            if (!linkOverlay) return;

            const promoItem = linkOverlay.closest(".js-ds-promo-card-item");
            const container = promoItem?.closest(".ds-promo-banner-new");
            if (!promoItem || !container) return;

            const isCarousel = container.classList.contains("ds-promo-banner-new--carousel");
            const widgetPosition = getWidgetPosition(container);
            let slideNumber: number | undefined;

            if (isCarousel) {
                const slides = Array.from(container.querySelectorAll(".js-ds-promo-card-item"));
                const idx = slides.indexOf(promoItem);
                if (idx >= 0) slideNumber = idx + 1;
            }

            const widgetType = isCarousel ? "carousel" : "banner";
            const promoData = extractPromoData(promoItem, widgetPosition, widgetType, slideNumber);

            DataLayer.pushInstant({
                event: "action.show.promos",
                action: {
                    show: {
                        promos: [promoData],
                    },
                },
            });
        },
        { passive: true }
    );
}

function setupCarouselTracking() {
    document.querySelectorAll(".ds-promo-banner-new--carousel").forEach((carousel) => {
        const widgetPosition = getWidgetPosition(carousel);

        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type !== "attributes" || mutation.attributeName !== "class") return;

                const target = mutation.target as Element;
                const isActive = target.classList.contains("js-ds-promo-card-item-active");
                const wasActive = (mutation as MutationRecord & { oldValue?: string }).oldValue?.includes(
                    "js-ds-promo-card-item-active"
                );

                if (!wasActive && isActive) {
                    const slides = Array.from(carousel.querySelectorAll(".js-ds-promo-card-item"));
                    const idx = slides.indexOf(target);
                    const slideNumber = idx >= 0 ? idx + 1 : undefined;
                    if (slideNumber) {
                        const promo = extractPromoData(target, widgetPosition, "carousel", slideNumber);
                        const key = promoKey(promo);
                        if (!impressedKeys.has(key)) {
                            impressedKeys.add(key);
                            trackImpressions([promo]);
                        }
                    }
                }
            });
        });

        carousel.querySelectorAll(".js-ds-promo-card-item").forEach((item) => {
            observer.observe(item, { attributes: true, attributeFilter: ["class"], attributeOldValue: true });
        });
    });
}

documentReady(initPromoCardAnalytics);
